import { useCallback, useState } from 'react';
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  SaveHint,
  Skeleton,
  useAutoSave,
  useDebouncedAutoSave,
} from '../../components/ui';
import { CompanySampleSubmissions } from '../supplierExchange/CompanySampleSubmissions';
import { useCompanySubmissions } from '../supplierExchange/sampleReview';
import { KeyValueRow, KeyValueText } from './KeyValueRows';
import { ProfileShell } from '../profiles/ProfileShell';
import { ProfileIdentity, ProfileLinkRow } from '../profiles/ProfileIdentity';
import { PropertyCard } from '../profiles/ProfileProperty';
import { NewLeadDrawer } from './LeadDrawers';
import { TaskFormDrawer } from './TasksView';
import {
  useCompanies,
  useCreateCompany,
  useDeleteCompany,
  useMergeCompanies,
  useSalesTasks,
  useUpdateCompany,
} from './hooks';
import type { CompanyUpdate } from './queries';
import { stageName } from './trackerFormat';
import {
  companyLeadsOf,
  companyStage,
  duplicateCompanyGroups,
  leadName,
  salesErrorMessage,
} from './types';
import type { Company, Lead, LeadStage, SalesTask } from './types';
import { DealLinkList } from './DealLinkList';
import { dealJumpHref, useDealsForCompany } from './leadDealLinks';
import { actorLabel, useLeadActivityFor } from './leadActivity';
import { useTapPeople } from '../projects/hooks';
import { useDealActivityFor } from '../deals/dealActivityFeed';
import { useOrdersWhere } from '../deals/dealOrdersList';
import { useCgStatuses } from '../cgOrders/hooks';
import { OUTCOME_LABELS } from '../deals/types';
import { formatAmount } from '../deals/dealAmount';
import { ProfileEmpty, ProfileRow } from '../profiles/ProfileSection';
import { orderHref } from '../profiles/profileLinks';
import { Timeline } from '../profiles/Timeline';
import {
  dealChangeItems,
  leadActivityItems,
  mergeTimeline,
  orderItems,
  salesTaskItems,
} from '../profiles/timelineItems';

/**
 * Exported since Drop 33: the lead panel's company-name link opens this same
 * drawer from the Leads tab, so TrackerPage renders it directly rather than
 * the Companies tab being the only door.
 */
export function CompanyPanel({
  company,
  leads,
  stages,
  onClose,
  onOpenLead,
}: {
  company: Company;
  leads: Lead[];
  stages: LeadStage[];
  onClose: () => void;
  /** Jumps to a lead from its chip; absent when there is nowhere to jump to. */
  onOpenLead?: (leadId: string) => void;
}) {
  const update = useUpdateCompany();
  const remove = useDeleteCompany();
  const tasksQuery = useSalesTasks();
  const [error, setError] = useState('');
  const [name, setName] = useState(company.name);
  const [notes, setNotes] = useState(company.notes ?? '');
  const [addingLead, setAddingLead] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [editingTask, setEditingTask] = useState<SalesTask | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Fetched for the count on the tab, so the tab tells you whether it is
  // worth opening rather than making you open it to find out.
  const sampleRuns = useCompanySubmissions(company.id);
  // Same reason: the Deals tab carries its count. Empty is the honest state
  // for most companies today — 41 of 53 deals had no company_id the day this
  // shipped — and the tab saying 0 is what makes the gap visible.
  const companyDeals = useDealsForCompany(company.id);

  const closeAddLead = useCallback(() => setAddingLead(false), []);
  const closeAddTask = useCallback(() => setAddingTask(false), []);

  const companyLeads = companyLeadsOf({ ...company, name }, leads);

  /*
    Drop 4 of docs/design-note-profiles-interlinked.md: the company's own
    CG orders (under Deals) and an Activity tab that gathers everyone and
    everything at the company. Kyu, 9 September, on HubSpot's company page:
    "you can see the deal or deals... active deals on top, most recent...
    all activities are there, most recent to oldest."

    Every source is already written; this is a read across the leads whose
    Company field is this company (companyLeadsOf, never the email domain),
    the deals whose Company is this company, and the orders likewise.
  */
  const companyOrders = useOrdersWhere('company_id', company.id);
  const cgStatuses = useCgStatuses('all');
  const statusLabel = (id: string) =>
    (cgStatuses.data ?? []).find((status) => status.id === id)?.label ?? id;
  const leadIds = companyLeads.map((lead) => lead.id);
  const leadHistory = useLeadActivityFor(leadIds);
  const dealIds = (companyDeals.data ?? []).map((deal) => deal.id);
  const dealChanges = useDealActivityFor(dealIds);
  const panelPeople = useTapPeople(true);
  const actorNames = new Map(
    (panelPeople.data ?? []).map((person) => [
      person.id,
      person.display_name === '' ? person.email : person.display_name,
    ]),
  );
  const personName = (id: string | null) =>
    id === null ? 'Nobody' : (actorNames.get(id) ?? 'Someone at TAP');
  const leadById = new Map(companyLeads.map((lead) => [lead.id, lead]));
  const companyTasks = (tasksQuery.data ?? []).filter(
    (task) =>
      task.company_id === company.id || (task.lead_id !== null && leadById.has(task.lead_id)),
  );
  const activity = mergeTimeline(
    // One builder call per lead, so each row carries that person's name.
    ...companyLeads.map((lead) =>
      leadActivityItems(
        (leadHistory.data ?? []).filter((row) => row.lead_id === lead.id),
        (row) => actorLabel(row, actorNames),
        leadName(lead),
      ),
    ),
    dealChangeItems(
      dealChanges.data ?? [],
      {
        stageName: (id) => (id === null ? 'No stage' : 'another stage'),
        sectionName: (id) => (id === null ? 'No section' : 'another section'),
        personName,
        amount: (value) => (value === null ? 'unpriced' : formatAmount(value)),
        outcome: (value) =>
          value === 'open' || value === 'won' || value === 'lost' ? OUTCOME_LABELS[value] : '—',
      },
      (dealId) => {
        const deal = (companyDeals.data ?? []).find((row) => row.id === dealId);
        return deal === undefined ? undefined : { name: deal.name, href: dealJumpHref(deal) };
      },
    ),
    salesTaskItems(companyTasks, personName),
    orderItems(companyOrders.data ?? [], statusLabel, orderHref),
  );
  const rollup = companyStage({ ...company, stage_override_id: null }, leads, stages);
  const openTasks = (tasksQuery.data ?? []).filter(
    (task) => task.company_id === company.id && task.status === 'open',
  );

  /** Every field writes through the same mutation; only the column differs. */
  const commit = useCallback(
    (changes: CompanyUpdate) =>
      new Promise((resolve, reject) => {
        setError('');
        update.mutate(
          { companyId: company.id, changes },
          {
            onSuccess: resolve,
            onError: (cause) => {
              setError(salesErrorMessage(cause.message));
              reject(cause);
            },
          },
        );
      }),
    [company.id, update],
  );

  const commitName = useCallback(
    (next: string) => {
      if (next.trim() === '') {
        return Promise.reject(new Error('The company needs a name.'));
      }
      setName(next.trim());
      return commit({ name: next.trim() });
    },
    [commit],
  );

  const commitDealValue = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      if (trimmed === '') {
        return commit({ deal_value: null });
      }
      const parsed = Number(trimmed.replace(/[$,]/g, ''));
      if (Number.isNaN(parsed)) {
        return Promise.reject(new Error('Deal value must be a number.'));
      }
      return commit({ deal_value: parsed });
    },
    [commit],
  );

  const stageSave = useAutoSave<string>(
    useCallback(
      (next: string) => commit({ stage_override_id: next === '' ? null : next }),
      [commit],
    ),
  );

  const notesSave = useDebouncedAutoSave<string>(
    useCallback((next: string) => commit({ notes: next }), [commit]),
  );

  return (
    <Drawer
      open
      // `wide` rather than the default: 45vw capped at 720px leaves the shell
      // about 678px, under the 760px two-column floor, so the three-column
      // layout would never appear at any window size. `wide` is 58vw capped
      // at 900, which leaves about 858. Three columns need 1060 and are still
      // out of reach until the 75% drawer exists; that width is Dev 2's.
      wide
      title={company.name}
      onClose={onClose}
      menuItems={[
        {
          label: confirmingDelete ? 'Confirm delete' : 'Delete company',
          danger: true,
          onSelect: () => {
            if (!confirmingDelete) {
              setConfirmingDelete(true);
              return;
            }
            setError('');
            remove.mutate(company.id, {
              onSuccess: onClose,
              onError: (cause) => setError(salesErrorMessage(cause.message)),
            });
          },
        },
      ]}
    >
      <div className="lead-panel-inner">
        {error !== '' && <InlineError>{error}</InlineError>}

        <ProfileShell
          identity={
            <ProfileIdentity>
              {/* Where this company stands, before anything is opened. */}
              <div className="company-panel-chips">
                <div className="company-panel-chips">
                  {rollup !== null && (
                    <Badge tone="brand" micro>
                      {String(rollup.stage.name)}
                    </Badge>
                  )}
                  <Badge
                    micro
                  >{`${companyLeads.length} ${companyLeads.length === 1 ? 'lead' : 'leads'}`}</Badge>
                  {openTasks.length > 0 && (
                    <Badge tone="warning" micro>{`${openTasks.length} open`}</Badge>
                  )}
                  {company.industry !== null && company.industry !== '' && (
                    <Badge micro>{company.industry}</Badge>
                  )}
                  {company.deal_value !== null && (
                    <Badge tone="success" micro>{`$${company.deal_value.toLocaleString()}`}</Badge>
                  )}
                </div>
              </div>
              {company.website === null || company.website.trim() === '' ? null : (
                <ProfileLinkRow
                  label="website"
                  value={company.website}
                  href={
                    company.website.startsWith('http')
                      ? company.website
                      : `https://${company.website}`
                  }
                />
              )}
              {confirmingDelete && (
                <p className="company-panel-confirm">
                  Deleting unlinks every lead from this company. Choose Confirm delete again to go
                  ahead.{' '}
                  <button
                    type="button"
                    className="tracker-lead-link"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Keep it
                  </button>
                </p>
              )}
            </ProfileIdentity>
          }
          properties={
            <PropertyCard title="About this company" icon="brief" tone="blue">
              <KeyValueText
                label="Name"
                value={company.name}
                onCommit={commitName}
                placeholder="Company name"
              />
              <KeyValueText
                label="Website"
                value={company.website ?? ''}
                placeholder="example.com"
                onCommit={(next) => commit({ website: next.trim() })}
              />
              <KeyValueText
                label="Industry"
                value={company.industry ?? ''}
                placeholder="Not set"
                onCommit={(next) => commit({ industry: next.trim() })}
              />
              <KeyValueText
                // "(hand-entered)" is the truth label the Deals tab made
                // necessary: the tab lists live deals, this field is a
                // number somebody typed, and the day they disagree the
                // screen must already say which is which. Approved in the
                // cross-navigation design note.
                label="Deal value (hand-entered)"
                value={company.deal_value === null ? '' : String(company.deal_value)}
                placeholder="Not set"
                onCommit={commitDealValue}
              />
              <KeyValueRow
                label="Stage"
                hint={<SaveHint state={stageSave.state} error={stageSave.error} />}
              >
                <select
                  className="kv-input"
                  aria-label="Stage"
                  defaultValue={company.stage_override_id ?? ''}
                  onChange={(event) => stageSave.save(event.target.value)}
                >
                  <option value="">
                    {`Auto — ${rollup === null ? 'no leads yet' : String(rollup.stage.name)}`}
                  </option>
                  {[...stages]
                    .sort((a, b) => a.position - b.position)
                    .map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {`${String(stage.name)} (override)`}
                      </option>
                    ))}
                </select>
              </KeyValueRow>
              <KeyValueRow
                label="Notes"
                hint={<SaveHint state={notesSave.state} error={notesSave.error} />}
              >
                <textarea
                  className="kv-input kv-input--multiline"
                  rows={3}
                  aria-label="Notes"
                  value={notes}
                  placeholder="Nothing noted yet"
                  onChange={(event) => {
                    setNotes(event.target.value);
                    notesSave.save(event.target.value);
                  }}
                />
              </KeyValueRow>
            </PropertyCard>
          }
          associations={
            <>
              {/*
                Five lists that used to be three tabs and two headings under a
                sixth. They are the same five lists; what changed is that a
                company with leads, deals, orders, tasks and samples no longer
                makes you pick one of them to look at.

                The Add buttons ride on the cards they belong to, through the
                actions slot, which is also how the inline style objects that
                laid them out beside a heading stopped being necessary.
              */}
              <PropertyCard
                title="Leads"
                icon="profile"
                tone="green"
                scrolls
                count={companyLeads.length}
                actions={
                  <Button variant="ghost" size="sm" onClick={() => setAddingLead(true)}>
                    + Add
                  </Button>
                }
              >
                {companyLeads.length === 0 ? (
                  <ProfileEmpty>No leads linked to this company.</ProfileEmpty>
                ) : (
                  <div className="entity-chips">
                    {companyLeads.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        className="entity-chip"
                        title={`${lead.title === '' ? leadName(lead) : lead.title} · ${stageName(stages, lead.stage_id)}`}
                        onClick={() => onOpenLead?.(lead.id)}
                      >
                        <span className="entity-chip-name">{leadName(lead)}</span>
                        <span className="entity-chip-meta">{stageName(stages, lead.stage_id)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </PropertyCard>

              <PropertyCard
                title="Deals"
                icon="deals"
                tone="purple"
                scrolls
                count={(companyDeals.data ?? []).length}
              >
                {/* Active deals first, then most recently closed: sortDealLinks,
                applied where the rows are read so every list agrees. */}
                <DealLinkList
                  deals={companyDeals.data}
                  isPending={companyDeals.isPending}
                  isError={companyDeals.isError}
                  emptyText="No deals are linked to this company. A deal gains one when its Company field is set."
                />
              </PropertyCard>

              <PropertyCard
                title="CG orders"
                icon="orders"
                tone="orange"
                scrolls
                count={(companyOrders.data ?? []).length}
              >
                {companyOrders.isError ? (
                  <ProfileEmpty>
                    CG orders could not be read. Only the CG team can see them.
                  </ProfileEmpty>
                ) : (companyOrders.data ?? []).length === 0 ? (
                  <ProfileEmpty>No CG orders are linked to this company.</ProfileEmpty>
                ) : (
                  (companyOrders.data ?? []).map((order) => (
                    <ProfileRow
                      key={order.id}
                      href={orderHref(order.id)}
                      name={`${order.order_no} · ${order.order_type === 'in_stock' ? 'In-Stock' : 'Custom'}`}
                      sub={order.client}
                      pill={{
                        label: statusLabel(order.status),
                        tone: order.date_delivered === null ? 'brand' : 'success',
                      }}
                      when={`placed ${order.created_at.slice(0, 10)}`}
                    />
                  ))
                )}
              </PropertyCard>

              <PropertyCard
                title="Open tasks"
                icon="tasks"
                tone="amber"
                scrolls
                count={openTasks.length}
                actions={
                  <Button variant="ghost" size="sm" onClick={() => setAddingTask(true)}>
                    + Add
                  </Button>
                }
              >
                {openTasks.length === 0 ? (
                  <ProfileEmpty>Nothing open for this company.</ProfileEmpty>
                ) : (
                  <div className="entity-chips">
                    {openTasks.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        className="entity-chip"
                        title={`Due ${task.due_on ?? 'no date set'}`}
                        onClick={() => setEditingTask(task)}
                      >
                        <span className="entity-chip-name">{task.title}</span>
                        <span className="entity-chip-meta">{task.due_on ?? 'no date'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </PropertyCard>

              <PropertyCard
                title="Sample submissions"
                icon="reviews"
                tone="orange"
                scrolls
                count={(sampleRuns.data ?? []).length}
              >
                <CompanySampleSubmissions companyId={company.id} />
              </PropertyCard>
            </>
          }
          timeline={
            <PropertyCard
              title="Activity"
              icon="clock"
              tone="grey"
              count={activity.length}
              hint="every contact, deal and order here"
            >
              {leadHistory.isError || dealChanges.isError ? (
                <ProfileEmpty>Some of this company's history could not be read.</ProfileEmpty>
              ) : (
                <Timeline items={activity} />
              )}
            </PropertyCard>
          }
        />
      </div>
      {addingLead && (
        <NewLeadDrawer
          open
          stages={stages}
          initialCompany={name}
          initialCompanyId={company.id}
          onClose={closeAddLead}
        />
      )}
      {addingTask && (
        <TaskFormDrawer
          task={null}
          leads={leads}
          companies={[company]}
          initialCompanyId={company.id}
          onClose={closeAddTask}
        />
      )}
      {editingTask !== null && (
        <TaskFormDrawer
          task={editingTask}
          leads={leads}
          companies={[company]}
          onClose={() => setEditingTask(null)}
        />
      )}
    </Drawer>
  );
}

function NewCompanyDrawer({ onClose }: { onClose: () => void }) {
  const create = useCreateCompany();
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');

  function submit() {
    if (name.trim() === '') {
      setError('The company needs a name.');
      return;
    }
    setError('');
    create.mutate(
      { name, website, industry },
      {
        onSuccess: onClose,
        onError: (cause) => setError(salesErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Drawer
      open
      title="New company"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add company'}
          </Button>
        </>
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Input label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
      <Input label="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)} />
    </Drawer>
  );
}

export function CompaniesView({
  leads,
  stages,
  initialCompanyId = null,
  onOpenLead,
}: {
  leads: Lead[];
  stages: LeadStage[];
  /** A company to open on arrival: the deal drawer's Open link. Read once. */
  initialCompanyId?: string | null;
  onOpenLead?: (leadId: string) => void;
}) {
  const companiesQuery = useCompanies();
  const merge = useMergeCompanies();
  const [openCompanyId, setOpenCompanyId] = useState<string | null>(initialCompanyId);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const companies = companiesQuery.data ?? [];

  const closeCreate = useCallback(() => setCreating(false), []);

  if (companiesQuery.isPending) return <Skeleton lines={3} label="Loading companies" />;

  const openCompany = companies.find((c) => c.id === openCompanyId) ?? null;

  const needle = search.trim().toLowerCase();
  const visible =
    needle === ''
      ? companies
      : companies.filter((company) =>
          [company.name, company.website ?? '', company.industry ?? '']
            .join(' ')
            .toLowerCase()
            .includes(needle),
        );
  const duplicates = duplicateCompanyGroups(companies);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ flex: '1 1 240px' }}>
          <Input
            label="Search companies"
            placeholder="Name, website, industry…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <Button onClick={() => setCreating(true)}>New company</Button>
      </div>
      {error !== '' && <InlineError>{error}</InlineError>}
      {visible.length === 0 ? (
        <EmptyState
          title={companies.length === 0 ? 'No companies yet' : 'No matches'}
          description={
            companies.length === 0 ? 'Add the first company.' : 'Try a different search.'
          }
        />
      ) : (
        <div className="tracker-table-scroll">
          <table className="tracker-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Stage</th>
                <th>Leads</th>
                <th>Website</th>
                <th>Industry</th>
                <th>Deal value</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((company) => {
                const stage = companyStage(company, leads, stages);
                const count = companyLeadsOf(company, leads).length;
                return (
                  <tr
                    key={company.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpenCompanyId(company.id)}
                  >
                    <td>
                      <button type="button" className="tracker-lead-link">
                        {company.name}
                      </button>
                    </td>
                    <td>
                      {stage === null ? (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      ) : (
                        <span className="company-cell-chips">
                          <Badge tone="brand" micro>
                            {String(stage.stage.name)}
                          </Badge>
                          {stage.overridden && <Badge micro>By hand</Badge>}
                        </span>
                      )}
                    </td>
                    <td>
                      {count === 0 ? <span style={{ color: 'var(--text-muted)' }}>—</span> : count}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{company.website}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{company.industry}</td>
                    <td>
                      {company.deal_value === null ? (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      ) : (
                        `$${company.deal_value.toLocaleString()}`
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Duplicate merge section */}
      {duplicates.length > 0 && (
        <div style={{ marginTop: 'var(--space-8)' }}>
          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--text-muted)',
              margin: '0 0 var(--space-4)',
            }}
          >
            Possible duplicates ({duplicates.length} groups)
          </p>
          {duplicates.map((group) => (
            <div
              key={group[0]?.id}
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-4)',
                marginBottom: 'var(--space-4)',
              }}
            >
              <p
                style={{
                  margin: '0 0 var(--space-3)',
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {group[0]?.name}
              </p>
              <table className="tracker-table">
                <tbody>
                  {group.map((company) => (
                    <tr key={company.id}>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {company.website === '' || company.website === null
                          ? '(no website)'
                          : company.website}
                      </td>
                      <td>{companyLeadsOf(company, leads).length} leads</td>
                      <td>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={merge.isPending}
                          onClick={() => {
                            setError('');
                            for (const loser of group.filter((c) => c.id !== company.id)) {
                              merge.mutate(
                                {
                                  keeper: { id: company.id, name: company.name },
                                  loser: { id: loser.id, name: loser.name },
                                },
                                {
                                  onError: (cause) => setError(salesErrorMessage(cause.message)),
                                },
                              );
                            }
                          }}
                        >
                          Keep this one
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {creating && <NewCompanyDrawer onClose={closeCreate} />}
      {openCompany !== null && (
        <CompanyPanel
          key={openCompany.id}
          company={openCompany}
          leads={leads}
          stages={stages}
          onClose={() => setOpenCompanyId(null)}
          {...(onOpenLead === undefined ? {} : { onOpenLead })}
        />
      )}
    </div>
  );
}

// ── Tasks ─────────────────────────────────────────────────────────────────────
